/**
 * Internationalization setup using i18next + react-i18next.
 *
 * Supported languages:
 *   - en: English (LTR)
 *   - fr: French (LTR)
 *   - ar: Arabic (RTL)
 *
 * Namespaces:
 *   - common     — shared labels, actions, statuses
 *   - dashboard  — dashboard page strings
 *   - knowledge  — knowledge base page strings
 *   - flows      — flow builder strings
 *   - alerts     — alerts page strings
 *   - crm        — CRM / conversations strings
 *   - llm        — LLM config page strings
 *   - appearance — appearance page strings
 *
 * Translation files:
 *   Inline objects for MVP (no separate JSON files).
 *   Extract to /public/locales/{lang}/{ns}.json when volume grows.
 *
 * RTL:
 *   - When language is 'ar', sets document.dir = 'rtl' and document.lang = 'ar'
 *   - Resets to 'ltr' for all other languages
 *
 * Persistence:
 *   - Language stored in localStorage under key 'helix_lang'
 *   - Falls back to browser language, then 'en'
 *
 * Exports:
 *   - i18n           — configured i18next instance
 *   - useTranslation — re-export from react-i18next
 *   - changeLanguage(lang) — helper that changes language + updates DOM dir
 */

import i18n from 'i18next';
import { initReactI18next, useTranslation as useTranslationBase } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

export { useTranslationBase as useTranslation };

// ---------------------------------------------------------------------------
// Type helpers
// ---------------------------------------------------------------------------

export type SupportedLanguage = 'en' | 'fr' | 'ar';

const RTL_LANGUAGES: SupportedLanguage[] = ['ar'];

// ---------------------------------------------------------------------------
// Translation resources
// ---------------------------------------------------------------------------

const resources = {
  en: {
    common: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      close: 'Close',
      confirm: 'Confirm',
      loading: 'Loading...',
      error: 'An error occurred',
      success: 'Success',
      warning: 'Warning',
      info: 'Info',
      search: 'Search',
      filter: 'Filter',
      export: 'Export',
      import: 'Import',
      refresh: 'Refresh',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      submit: 'Submit',
      reset: 'Reset',
      copy: 'Copy',
      copied: 'Copied!',
      actions: 'Actions',
      status: 'Status',
      name: 'Name',
      date: 'Date',
      type: 'Type',
      size: 'Size',
      noData: 'No data available',
      noResults: 'No results found',
      required: 'This field is required',
      optional: 'Optional',
      enabled: 'Enabled',
      disabled: 'Disabled',
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
      processing: 'Processing',
      done: 'Done',
      failed: 'Failed',
      unknown: 'Unknown',
      unsavedChanges: 'You have unsaved changes',
      savedSuccessfully: 'Saved successfully',
      deletedSuccessfully: 'Deleted successfully',
      confirmDelete: 'Are you sure you want to delete this?',
      thisActionCannotBeUndone: 'This action cannot be undone.',
      rowsPerPage: 'Rows per page',
      of: 'of',
      page: 'Page',
    },
    dashboard: {
      title: 'Dashboard',
      overview: 'Overview',
      liveWall: 'Live Wall',
      totalConversations: 'Total Conversations',
      avgResponseTime: 'Avg. Response Time',
      escalationRate: 'Escalation Rate',
      satisfactionScore: 'Satisfaction Score',
      activeConversations: 'Active Conversations',
      conversationsToday: 'Conversations Today',
      last7Days: 'Last 7 days',
      last30Days: 'Last 30 days',
    },
    knowledge: {
      title: 'Knowledge Base',
      uploadDocument: 'Upload Document',
      dragAndDrop: 'Drag and drop files here',
      orClickToBrowse: 'or click to browse',
      supportedFormats: 'Supported: PDF, DOCX, TXT, MD, HTML',
      maxFileSize: 'Max file size: 50MB',
      testQuery: 'Test Query',
      runQuery: 'Run Query',
      queryPlaceholder: 'Enter a question to test retrieval...',
      chunkPreview: 'Chunk Preview',
      relevanceScore: 'Relevance Score',
      processingDocument: 'Processing document...',
      documentReady: 'Document ready',
      uploadFailed: 'Upload failed',
    },
    flows: {
      title: 'Flow Builder',
      newFlow: 'New Flow',
      saveFlow: 'Save Flow',
      testFlow: 'Test Flow',
      publishFlow: 'Publish Flow',
      flowName: 'Flow Name',
      addNode: 'Add Node',
      nodeTypes: {
        message: 'Message',
        condition: 'Condition',
        action: 'Action',
        escalate: 'Escalate',
        end: 'End',
      },
      validationErrors: 'Flow has validation errors',
    },
    alerts: {
      title: 'Alerts',
      createRule: 'Create Rule',
      alertRules: 'Alert Rules',
      alertHistory: 'Alert History',
      triggered: 'Triggered',
      resolved: 'Resolved',
      severity: {
        low: 'Low',
        medium: 'Medium',
        high: 'High',
        critical: 'Critical',
      },
      channels: {
        email: 'Email',
        slack: 'Slack',
        webhook: 'Webhook',
      },
    },
    crm: {
      title: 'Customers',
      conversations: 'Conversations',
      customerProfile: 'Customer Profile',
      conversationHistory: 'Conversation History',
      exportData: 'Export Data',
      sentiment: {
        positive: 'Positive',
        neutral: 'Neutral',
        negative: 'Negative',
      },
      tier: {
        free: 'Free',
        pro: 'Pro',
        enterprise: 'Enterprise',
      },
    },
    llm: {
      title: 'AI Model Configuration',
      roles: {
        orchestrator: 'Orchestrator',
        agent: 'Agent',
        classifier: 'Classifier',
      },
      provider: 'Provider',
      model: 'Model',
      apiKey: 'API Key',
      temperature: 'Temperature',
      maxTokens: 'Max Tokens',
      endpointUrl: 'Endpoint URL',
      testConnection: 'Test Connection',
      testAll: 'Test All',
      connectionSuccess: 'Connection successful',
      connectionFailed: 'Connection failed',
      configSaved: 'Configuration saved',
    },
    appearance: {
      title: 'Appearance',
      colorPalette: 'Color Palette',
      typography: 'Typography',
      logo: 'Logo',
      chatBubbleStyle: 'Chat Bubble Style',
      livePreview: 'Live Preview',
      primaryColor: 'Primary Color',
      secondaryColor: 'Secondary Color',
      accentColor: 'Accent Color',
      surfaceColor: 'Surface Color',
      headingFont: 'Heading Font',
      bodyFont: 'Body Font',
      uploadLogo: 'Upload Logo',
      bubbleStyles: {
        rounded: 'Rounded',
        flat: 'Flat',
        sharp: 'Sharp',
        floating: 'Floating',
      },
      rtlNote: 'Arabic layout will use right-to-left direction',
    },
  },

  fr: {
    common: {
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      create: 'Créer',
      close: 'Fermer',
      confirm: 'Confirmer',
      loading: 'Chargement...',
      error: 'Une erreur est survenue',
      success: 'Succès',
      warning: 'Avertissement',
      info: 'Information',
      search: 'Rechercher',
      filter: 'Filtrer',
      export: 'Exporter',
      import: 'Importer',
      refresh: 'Actualiser',
      back: 'Retour',
      next: 'Suivant',
      previous: 'Précédent',
      submit: 'Soumettre',
      reset: 'Réinitialiser',
      copy: 'Copier',
      copied: 'Copié !',
      actions: 'Actions',
      status: 'Statut',
      name: 'Nom',
      date: 'Date',
      type: 'Type',
      size: 'Taille',
      noData: 'Aucune donnée disponible',
      noResults: 'Aucun résultat trouvé',
      required: 'Ce champ est obligatoire',
      optional: 'Optionnel',
      enabled: 'Activé',
      disabled: 'Désactivé',
      active: 'Actif',
      inactive: 'Inactif',
      pending: 'En attente',
      processing: 'En traitement',
      done: 'Terminé',
      failed: 'Échec',
      unknown: 'Inconnu',
      unsavedChanges: 'Vous avez des modifications non enregistrées',
      savedSuccessfully: 'Enregistré avec succès',
      deletedSuccessfully: 'Supprimé avec succès',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer ceci ?',
      thisActionCannotBeUndone: 'Cette action est irréversible.',
      rowsPerPage: 'Lignes par page',
      of: 'sur',
      page: 'Page',
    },
    dashboard: {
      title: 'Tableau de bord',
      overview: 'Vue d\'ensemble',
      liveWall: 'Mur en direct',
      totalConversations: 'Conversations totales',
      avgResponseTime: 'Temps de réponse moyen',
      escalationRate: 'Taux d\'escalade',
      satisfactionScore: 'Score de satisfaction',
      activeConversations: 'Conversations actives',
      conversationsToday: 'Conversations aujourd\'hui',
      last7Days: '7 derniers jours',
      last30Days: '30 derniers jours',
    },
    knowledge: {
      title: 'Base de connaissances',
      uploadDocument: 'Télécharger un document',
      dragAndDrop: 'Glissez et déposez des fichiers ici',
      orClickToBrowse: 'ou cliquez pour parcourir',
      supportedFormats: 'Formats supportés : PDF, DOCX, TXT, MD, HTML',
      maxFileSize: 'Taille maximale : 50 Mo',
      testQuery: 'Tester la requête',
      runQuery: 'Exécuter',
      queryPlaceholder: 'Entrez une question pour tester la récupération...',
      chunkPreview: 'Aperçu du fragment',
      relevanceScore: 'Score de pertinence',
      processingDocument: 'Traitement du document...',
      documentReady: 'Document prêt',
      uploadFailed: 'Échec du téléchargement',
    },
    flows: {
      title: 'Constructeur de flux',
      newFlow: 'Nouveau flux',
      saveFlow: 'Enregistrer le flux',
      testFlow: 'Tester le flux',
      publishFlow: 'Publier le flux',
      flowName: 'Nom du flux',
      addNode: 'Ajouter un nœud',
      nodeTypes: {
        message: 'Message',
        condition: 'Condition',
        action: 'Action',
        escalate: 'Escalade',
        end: 'Fin',
      },
      validationErrors: 'Le flux contient des erreurs de validation',
    },
    alerts: {
      title: 'Alertes',
      createRule: 'Créer une règle',
      alertRules: 'Règles d\'alerte',
      alertHistory: 'Historique des alertes',
      triggered: 'Déclenchée',
      resolved: 'Résolue',
      severity: {
        low: 'Faible',
        medium: 'Moyenne',
        high: 'Élevée',
        critical: 'Critique',
      },
      channels: {
        email: 'E-mail',
        slack: 'Slack',
        webhook: 'Webhook',
      },
    },
    crm: {
      title: 'Clients',
      conversations: 'Conversations',
      customerProfile: 'Profil client',
      conversationHistory: 'Historique des conversations',
      exportData: 'Exporter les données',
      sentiment: {
        positive: 'Positif',
        neutral: 'Neutre',
        negative: 'Négatif',
      },
      tier: {
        free: 'Gratuit',
        pro: 'Pro',
        enterprise: 'Entreprise',
      },
    },
    llm: {
      title: 'Configuration des modèles IA',
      roles: {
        orchestrator: 'Orchestrateur',
        agent: 'Agent',
        classifier: 'Classificateur',
      },
      provider: 'Fournisseur',
      model: 'Modèle',
      apiKey: 'Clé API',
      temperature: 'Température',
      maxTokens: 'Tokens max',
      endpointUrl: 'URL du point de terminaison',
      testConnection: 'Tester la connexion',
      testAll: 'Tout tester',
      connectionSuccess: 'Connexion réussie',
      connectionFailed: 'Échec de la connexion',
      configSaved: 'Configuration enregistrée',
    },
    appearance: {
      title: 'Apparence',
      colorPalette: 'Palette de couleurs',
      typography: 'Typographie',
      logo: 'Logo',
      chatBubbleStyle: 'Style de bulle de chat',
      livePreview: 'Aperçu en direct',
      primaryColor: 'Couleur principale',
      secondaryColor: 'Couleur secondaire',
      accentColor: 'Couleur d\'accentuation',
      surfaceColor: 'Couleur de surface',
      headingFont: 'Police des titres',
      bodyFont: 'Police du corps',
      uploadLogo: 'Télécharger le logo',
      bubbleStyles: {
        rounded: 'Arrondi',
        flat: 'Plat',
        sharp: 'Anguleux',
        floating: 'Flottant',
      },
      rtlNote: 'La mise en page arabe utilisera la direction droite-à-gauche',
    },
  },

  ar: {
    common: {
      save: 'حفظ',
      cancel: 'إلغاء',
      delete: 'حذف',
      edit: 'تعديل',
      create: 'إنشاء',
      close: 'إغلاق',
      confirm: 'تأكيد',
      loading: 'جارٍ التحميل...',
      error: 'حدث خطأ',
      success: 'نجاح',
      warning: 'تحذير',
      info: 'معلومة',
      search: 'بحث',
      filter: 'تصفية',
      export: 'تصدير',
      import: 'استيراد',
      refresh: 'تحديث',
      back: 'رجوع',
      next: 'التالي',
      previous: 'السابق',
      submit: 'إرسال',
      reset: 'إعادة تعيين',
      copy: 'نسخ',
      copied: 'تم النسخ!',
      actions: 'إجراءات',
      status: 'الحالة',
      name: 'الاسم',
      date: 'التاريخ',
      type: 'النوع',
      size: 'الحجم',
      noData: 'لا توجد بيانات',
      noResults: 'لا توجد نتائج',
      required: 'هذا الحقل مطلوب',
      optional: 'اختياري',
      enabled: 'مفعّل',
      disabled: 'معطّل',
      active: 'نشط',
      inactive: 'غير نشط',
      pending: 'قيد الانتظار',
      processing: 'قيد المعالجة',
      done: 'مكتمل',
      failed: 'فشل',
      unknown: 'غير معروف',
      unsavedChanges: 'لديك تغييرات غير محفوظة',
      savedSuccessfully: 'تم الحفظ بنجاح',
      deletedSuccessfully: 'تم الحذف بنجاح',
      confirmDelete: 'هل أنت متأكد من رغبتك في الحذف؟',
      thisActionCannotBeUndone: 'لا يمكن التراجع عن هذا الإجراء.',
      rowsPerPage: 'صفوف في الصفحة',
      of: 'من',
      page: 'صفحة',
    },
    dashboard: {
      title: 'لوحة التحكم',
      overview: 'نظرة عامة',
      liveWall: 'الجدار المباشر',
      totalConversations: 'إجمالي المحادثات',
      avgResponseTime: 'متوسط وقت الاستجابة',
      escalationRate: 'معدل التصعيد',
      satisfactionScore: 'درجة الرضا',
      activeConversations: 'المحادثات النشطة',
      conversationsToday: 'محادثات اليوم',
      last7Days: 'آخر 7 أيام',
      last30Days: 'آخر 30 يومًا',
    },
    knowledge: {
      title: 'قاعدة المعرفة',
      uploadDocument: 'رفع مستند',
      dragAndDrop: 'اسحب وأفلت الملفات هنا',
      orClickToBrowse: 'أو انقر للاستعراض',
      supportedFormats: 'الصيغ المدعومة: PDF، DOCX، TXT، MD، HTML',
      maxFileSize: 'الحجم الأقصى: 50 ميجابايت',
      testQuery: 'اختبار الاستعلام',
      runQuery: 'تشغيل',
      queryPlaceholder: 'أدخل سؤالاً لاختبار الاسترجاع...',
      chunkPreview: 'معاينة المقطع',
      relevanceScore: 'درجة الصلة',
      processingDocument: 'جارٍ معالجة المستند...',
      documentReady: 'المستند جاهز',
      uploadFailed: 'فشل الرفع',
    },
    flows: {
      title: 'منشئ التدفقات',
      newFlow: 'تدفق جديد',
      saveFlow: 'حفظ التدفق',
      testFlow: 'اختبار التدفق',
      publishFlow: 'نشر التدفق',
      flowName: 'اسم التدفق',
      addNode: 'إضافة عقدة',
      nodeTypes: {
        message: 'رسالة',
        condition: 'شرط',
        action: 'إجراء',
        escalate: 'تصعيد',
        end: 'نهاية',
      },
      validationErrors: 'يحتوي التدفق على أخطاء تحقق',
    },
    alerts: {
      title: 'التنبيهات',
      createRule: 'إنشاء قاعدة',
      alertRules: 'قواعد التنبيه',
      alertHistory: 'سجل التنبيهات',
      triggered: 'مُشغَّل',
      resolved: 'محلول',
      severity: {
        low: 'منخفض',
        medium: 'متوسط',
        high: 'مرتفع',
        critical: 'حرج',
      },
      channels: {
        email: 'البريد الإلكتروني',
        slack: 'سلاك',
        webhook: 'ويب هوك',
      },
    },
    crm: {
      title: 'العملاء',
      conversations: 'المحادثات',
      customerProfile: 'ملف العميل',
      conversationHistory: 'سجل المحادثات',
      exportData: 'تصدير البيانات',
      sentiment: {
        positive: 'إيجابي',
        neutral: 'محايد',
        negative: 'سلبي',
      },
      tier: {
        free: 'مجاني',
        pro: 'احترافي',
        enterprise: 'مؤسسي',
      },
    },
    llm: {
      title: 'إعداد نماذج الذكاء الاصطناعي',
      roles: {
        orchestrator: 'المنسق',
        agent: 'الوكيل',
        classifier: 'المصنِّف',
      },
      provider: 'المزود',
      model: 'النموذج',
      apiKey: 'مفتاح API',
      temperature: 'درجة الحرارة',
      maxTokens: 'الحد الأقصى للرموز',
      endpointUrl: 'عنوان URL للنقطة الطرفية',
      testConnection: 'اختبار الاتصال',
      testAll: 'اختبار الكل',
      connectionSuccess: 'الاتصال ناجح',
      connectionFailed: 'فشل الاتصال',
      configSaved: 'تم حفظ الإعداد',
    },
    appearance: {
      title: 'المظهر',
      colorPalette: 'لوحة الألوان',
      typography: 'الخطوط',
      logo: 'الشعار',
      chatBubbleStyle: 'نمط فقاعة الدردشة',
      livePreview: 'معاينة مباشرة',
      primaryColor: 'اللون الأساسي',
      secondaryColor: 'اللون الثانوي',
      accentColor: 'لون التمييز',
      surfaceColor: 'لون السطح',
      headingFont: 'خط العناوين',
      bodyFont: 'خط النص',
      uploadLogo: 'رفع الشعار',
      bubbleStyles: {
        rounded: 'مدور',
        flat: 'مسطح',
        sharp: 'حاد',
        floating: 'عائم',
      },
      rtlNote: 'سيستخدم التخطيط العربي الاتجاه من اليمين إلى اليسار',
    },
  },
};

// ---------------------------------------------------------------------------
// i18next initialization
// ---------------------------------------------------------------------------

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common', 'dashboard', 'knowledge', 'flows', 'alerts', 'crm', 'llm', 'appearance'],
    interpolation: {
      escapeValue: false, // React already handles XSS
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'helix_lang',
      caches: ['localStorage'],
    },
  });

// Apply RTL on init if needed
const currentLang = i18n.language as SupportedLanguage;
if (RTL_LANGUAGES.includes(currentLang)) {
  document.documentElement.dir = 'rtl';
  document.documentElement.lang = currentLang;
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { i18n as default };

/**
 * Helper: change language and update DOM direction.
 * Use this instead of i18n.changeLanguage() to ensure RTL is applied.
 */
export async function changeLanguage(lang: SupportedLanguage): Promise<void> {
  await i18n.changeLanguage(lang);
  const isRTL = RTL_LANGUAGES.includes(lang);
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  document.documentElement.lang = lang;
  localStorage.setItem('helix_lang', lang);
}
